import React, { useState, useEffect } from 'react';
import './AddProductModal.css';

const AddProductModal = ({ isOpen, onClose, onAddProduct, categories }) => {
  const [formData, setFormData] = useState({
    titolo: '',
    descrizione: '',
    prezzo: '',
    quantita: '0',
    id_categoria: '',
    attivo: true,
    foto: null,
    fotoPreview: null,
    fotoAggiuntive: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Resetta il form quando il modal si apre o chiude
  useEffect(() => {
    if (isOpen) {
      setFormData({
        titolo: '',
        descrizione: '',
        prezzo: '',
        quantita: '0',
        id_categoria: '',
        attivo: true,
        foto: null,
        fotoPreview: null,
        fotoAggiuntive: []
      });
      setError(null);
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file' && files && files.length > 0) {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData({
          ...formData,
          [name]: file,
          fotoPreview: e.target.result
        });
      };
      reader.readAsDataURL(file);
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value,
      });
    }
  };

  const handleAdditionalImages = (e) => {
    const files = Array.from(e.target.files);
    const readers = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve({ file, preview: e.target.result });
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readers).then(previews => {
      setFormData({
        ...formData,
        fotoAggiuntive: [...formData.fotoAggiuntive, ...previews].slice(0, 3) // Max 3 immagini aggiuntive
      });
    });
  };

  const removeAdditionalImage = (index) => {
    setFormData({
      ...formData,
      fotoAggiuntive: formData.fotoAggiuntive.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const API_BASE_URL = process.env.NODE_ENV === 'production' 
      ? '/shop/backend/api' 
      : 'http://efwypvu.cluster021.hosting.ovh.net/shop/backend/api';

    try {
      // Validazioni
      if (!formData.titolo.trim()) {
        throw new Error('Il titolo è obbligatorio');
      }

      if (!formData.descrizione.trim()) {
        throw new Error('La descrizione è obbligatoria');
      }

      if (!formData.prezzo || parseFloat(formData.prezzo) <= 0) {
        throw new Error('Il prezzo deve essere maggiore di 0');
      }

      if (!formData.id_categoria) {
        throw new Error('La categoria è obbligatoria');
      }

      const quantityValue = parseInt(formData.quantita, 10);
      if (Number.isNaN(quantityValue) || quantityValue < 0) {
        throw new Error('La quantità deve essere maggiore o uguale a 0');
      }

      console.log('📸 Inizio upload immagini...');
      
      let folderName = null;
      
      // 1. Upload immagine principale se presente
      if (formData.foto) {
        console.log('📤 Upload immagine principale...');
        const formDataImage = new FormData();
        formDataImage.append('image', formData.foto);
        formDataImage.append('product_name', formData.titolo);
        formDataImage.append('image_type', 'main');
        
        const uploadResponse = await fetch(`${API_BASE_URL}/upload_image.php`, {
          method: 'POST',
          body: formDataImage
        });
        
        const uploadData = await uploadResponse.json();
        console.log('📋 Risposta upload principale:', uploadData);
        
        if (!uploadData.success) {
          throw new Error(uploadData.error || 'Errore upload immagine principale');
        }
        
        folderName = uploadData.data.folder_name;
        console.log('✅ Immagine principale caricata! Cartella:', folderName);
      }
      
      // 2. Upload immagini aggiuntive se presenti
      if (formData.fotoAggiuntive && formData.fotoAggiuntive.length > 0) {
        console.log('📤 Upload', formData.fotoAggiuntive.length, 'immagini aggiuntive...');
        
        for (let i = 0; i < formData.fotoAggiuntive.length; i++) {
          const imgData = formData.fotoAggiuntive[i];
          const formDataImage = new FormData();
          formDataImage.append('image', imgData.file);
          formDataImage.append('product_name', formData.titolo);
          formDataImage.append('image_type', 'additional');
          
          const uploadResponse = await fetch(`${API_BASE_URL}/upload_image.php`, {
            method: 'POST',
            body: formDataImage
          });
          
          const uploadData = await uploadResponse.json();
          console.log(`📋 Risposta upload immagine ${i + 1}:`, uploadData);
          
          if (!uploadData.success) {
            console.warn(`⚠️ Errore upload immagine ${i + 1}:`, uploadData.error);
          } else {
            if (!folderName) {
              folderName = uploadData.data.folder_name;
            }
            console.log(`✅ Immagine ${i + 1} caricata!`);
          }
        }
      }
      
      // 3. Crea il prodotto nel database
      console.log('💾 Creazione prodotto nel database...');
      const submitData = {
        titolo: formData.titolo.trim(),
        descrizione: formData.descrizione.trim(),
        prezzo: parseFloat(formData.prezzo),
        quantita: quantityValue,
        id_categoria: parseInt(formData.id_categoria),
        attivo: formData.attivo ? 1 : 0,
        foto: folderName // Salva il nome della cartella
      };

      await onAddProduct(submitData);
      console.log('✅ Prodotto creato con successo!');
      onClose(); // Chiudi il modal al successo
    } catch (err) {
      console.error('💥 Errore:', err);
      setError(err.message || 'Errore durante l\'aggiunta del prodotto');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="product-modal-overlay" onClick={onClose}>
      <div className="product-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* X nell'angolo in alto a destra */}
        <button className="product-modal-close-button" onClick={onClose}>
          ×
        </button>
        
        <div className="product-modal-header">
          <h2>Aggiungi Prodotto</h2>
        </div>
        
        <div className="product-modal-body">
          <form onSubmit={handleSubmit} className="product-modal-form">
            <div className="form-layout">
              {/* Sezione Sinistra - Form */}
              <div className="form-left">
                <div className="form-group">
                  <label htmlFor="titolo">Titolo Prodotto *</label>
                  <input
                    type="text"
                    id="titolo"
                    name="titolo"
                    value={formData.titolo}
                    onChange={handleChange}
                    required
                    placeholder="Inserisci titolo prodotto"
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="descrizione">Descrizione *</label>
                  <textarea
                    id="descrizione"
                    name="descrizione"
                    value={formData.descrizione}
                    onChange={handleChange}
                    required
                    placeholder="Inserisci descrizione prodotto"
                    rows="3"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="prezzo">Prezzo (€) *</label>
                    <input
                      type="number"
                      id="prezzo"
                      name="prezzo"
                      value={formData.prezzo}
                      onChange={handleChange}
                      required
                      step="0.01"
                      min="0.01"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="quantita">Quantità *</label>
                    <input
                      type="number"
                      id="quantita"
                      name="quantita"
                      value={formData.quantita}
                      onChange={handleChange}
                      required
                      min="0"
                      step="1"
                      placeholder="0"
                    />
                  </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="id_categoria">Categoria *</label>
                    <select
                      id="id_categoria"
                      name="id_categoria"
                      value={formData.id_categoria}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Seleziona categoria</option>
                      {categories.map((category) => (
                        <option key={category.id_categoria} value={category.id_categoria}>
                          {category.nome}
                        </option>
                      ))}
                    </select>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="attivo"
                      checked={formData.attivo}
                      onChange={handleChange}
                    />
                    <span className="checkbox-custom"></span>
                    Prodotto attivo
                  </label>
                  <small className="form-help">
                    I prodotti inattivi non saranno visibili nello shop
                  </small>
                </div>
              </div>

              {/* Sezione Destra - Immagini */}
              <div className="form-right">
                <div className="images-section">
                  <h3>Immagini Prodotto</h3>
                  
                  {/* Immagine Principale */}
                  <div className="main-image-container">
                    <label htmlFor="foto" className="main-image-upload">
                      {formData.fotoPreview ? (
                        <img src={formData.fotoPreview} alt="Preview" className="main-image-preview" />
                      ) : (
                        <div className="main-image-placeholder">
                          <i className="fas fa-camera"></i>
                          <span>Immagine Principale</span>
                        </div>
                      )}
                    </label>
                    <input
                      type="file"
                      id="foto"
                      name="foto"
                      onChange={handleChange}
                      accept="image/*"
                      style={{ display: 'none' }}
                    />
                  </div>

                  {/* Immagini Aggiuntive */}
                  <div className="additional-images">
                    <label htmlFor="fotoAggiuntive" className="additional-upload">
                      <i className="fas fa-plus"></i>
                      <span>Aggiungi Foto</span>
                    </label>
                    <input
                      type="file"
                      id="fotoAggiuntive"
                      onChange={handleAdditionalImages}
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                    />
                    
                    <div className="additional-images-grid">
                      {formData.fotoAggiuntive.map((image, index) => (
                        <div key={index} className="additional-image-item">
                          <img src={image.preview} alt={`Preview ${index + 1}`} />
                          <button
                            type="button"
                            className="remove-image"
                            onClick={() => removeAdditionalImage(index)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                      {Array.from({ length: 3 - formData.fotoAggiuntive.length }).map((_, index) => (
                        <div key={`empty-${index}`} className="additional-image-placeholder">
                          <i className="fas fa-image"></i>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="product-error-message">
                ⚠️ {error}
              </div>
            )}

            <div className="product-modal-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onClose}
                disabled={loading}
              >
                Annulla
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Aggiunta in corso...' : 'Aggiungi Prodotto'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddProductModal;
